// Minimal client state store using Zustand
// Server state (plans, property data) handled by TanStack Query

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AddressData, PersonalInfo, SelectedService, PropertyData, AppFieldResponse, WaterPlan, DocumentUploadFlags } from '@/types';
import type { FlowType } from '@/config/abTest';
import { logger } from '@/utils/logger';

// Persist full PersonalInfo to sessionStorage (session-scoped, clears on tab close)
function sanitizePersonalInfo(info: PersonalInfo): PersonalInfo {
  return info;
}

// Configured plan with service date and options
export interface ConfiguredPlan {
  planId: string;
  serviceDate: string;
  // Holiday date strings (YYYY-MM-DD) used by the date picker when this date was chosen.
  // Persisted so checkout re-validation uses the same calendar the customer saw.
  holidays?: string[];
  options: Array<{
    id: string;
    type: 'check' | 'radio' | 'single';
    value: string | boolean | number;
  }>;
}

// Apartment water management choices
export type ApartmentWaterChoice = 'property-managed' | 'self-managed' | null;

interface OrderState {
  // User session
  userId: string | null;

  // A/B test flow type
  flowType: FlowType;

  // Address & property
  address: AddressData | null;
  propertyData: PropertyData | null;
  isApartment: boolean;
  apartmentWaterChoice: ApartmentWaterChoice;

  // Personal info
  personalInfo: PersonalInfo | null;

  // Service selection
  selectedServices: SelectedService[];
  waterAvailable: boolean;
  waterDeclined: boolean;  // True when user explicitly removes water

  // Plan configuration flow
  planOptionsQueue: string[]; // planIds waiting to be configured
  configuredPlans: ConfiguredPlan[];

  // Checkout form fields (vendor-specific questions like SSN, DL#, etc.)
  appFields: AppFieldResponse[];

  // ONIT Water Filtration
  onitPlan: WaterPlan | null;       // Cached ONIT plan from API
  onitSelected: boolean;            // Whether user wants ONIT

  // Document upload flags
  uploadFlags: DocumentUploadFlags;

  // Order result
  confirmationId: string | null;
  electricityCompletionLink: string | null;

}

interface OrderActions {
  // User
  setUserId: (id: string) => void;

  // A/B test
  setFlowType: (flowType: FlowType) => void;

  // Address
  setAddress: (address: AddressData) => void;
  setPropertyData: (data: PropertyData | null) => void;
  setIsApartment: (isApartment: boolean) => void;
  setApartmentWaterChoice: (choice: ApartmentWaterChoice) => void;

  // Personal info
  setPersonalInfo: (info: PersonalInfo) => void;

  // Services
  setWaterAvailable: (available: boolean) => void;
  setWaterDeclined: (declined: boolean) => void;
  addService: (service: SelectedService) => void;
  removeService: (planId: string) => void;
  clearServices: () => void;

  // Plan options flow
  setPlanOptionsQueue: (planIds: string[]) => void;
  markPlanConfigured: (config: ConfiguredPlan) => void;
  getNextPlanId: (currentPlanId: string) => string | null;

  // Checkout fields
  setAppFields: (fields: AppFieldResponse[]) => void;

  // ONIT Water Filtration
  setOnitPlan: (plan: WaterPlan | null) => void;
  setOnitSelected: (selected: boolean) => void;

  // Document upload flags
  setUploadFlags: (flags: DocumentUploadFlags) => void;

  // Order complete
  setConfirmationId: (id: string) => void;
  setElectricityCompletionLink: (link: string | null) => void;

  // Reset
  reset: () => void;
}

const initialState: OrderState = {
  userId: null,
  flowType: 'A',
  address: null,
  propertyData: null,
  isApartment: false,
  apartmentWaterChoice: null,
  personalInfo: null,
  selectedServices: [],
  waterAvailable: false,
  waterDeclined: false,
  planOptionsQueue: [],
  configuredPlans: [],
  appFields: [],
  onitPlan: null,
  onitSelected: true,  // Default checked
  uploadFlags: { isDLUpload: false, isLeaseUpload: false, isOwnUpload: false },
  confirmationId: null,
  electricityCompletionLink: null,
};

export const useOrderStore = create<OrderState & OrderActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      // User
      setUserId: (id) => set({ userId: id }),

      // A/B test
      setFlowType: (flowType) => set({ flowType }),

      // Address
      setAddress: (address) => set({ address }),
      setPropertyData: (data) => set({ propertyData: data }),
      setIsApartment: (isApartment) => set({ isApartment }),
      setApartmentWaterChoice: (choice) => set({ apartmentWaterChoice: choice }),

      // Personal info
      setPersonalInfo: (info) => set({ personalInfo: info }),

      // Services
      setWaterAvailable: (available) => set({ waterAvailable: available }),
      setWaterDeclined: (declined) => set({ waterDeclined: declined }),

      addService: (service) =>
        set((state) => {
          // Don't add duplicates
          if (state.selectedServices.some((s) => s.planId === service.planId)) {
            return state;
          }
          return { selectedServices: [...state.selectedServices, service] };
        }),

      removeService: (planId) =>
        set((state) => ({
          selectedServices: state.selectedServices.filter((s) => s.planId !== planId),
          // Also remove from configured plans
          configuredPlans: state.configuredPlans.filter((c) => c.planId !== planId),
        })),

      clearServices: () => set({ selectedServices: [], configuredPlans: [] }),

      // Plan options flow
      setPlanOptionsQueue: (planIds) => set({ planOptionsQueue: planIds }),

      markPlanConfigured: (config) =>
        set((state) => ({
          configuredPlans: [
            ...state.configuredPlans.filter((c) => c.planId !== config.planId),
            config,
          ],
        })),

      getNextPlanId: (currentPlanId) => {
        const { planOptionsQueue } = get();
        const currentIndex = planOptionsQueue.indexOf(currentPlanId);
        if (currentIndex === -1 || currentIndex >= planOptionsQueue.length - 1) {
          return null;
        }
        return planOptionsQueue[currentIndex + 1];
      },

      // Checkout fields
      setAppFields: (fields) => set({ appFields: fields }),

      // ONIT Water Filtration
      setOnitPlan: (plan) => set({ onitPlan: plan }),
      setOnitSelected: (selected) => set({ onitSelected: selected }),

      // Document upload flags
      setUploadFlags: (flags) => set({ uploadFlags: flags }),

      // Order complete
      setConfirmationId: (id) => set({ confirmationId: id }),
      setElectricityCompletionLink: (link) => set({ electricityCompletionLink: link }),

      // Reset entire store
      reset: () => set(initialState),
    }),
    {
      name: 'order-storage',
      storage: createJSONStorage(() => sessionStorage), // Session-only persistence (clears on tab close)
      partialize: (state) => ({
        userId: state.userId,
        flowType: state.flowType,
        address: state.address,
        personalInfo: state.personalInfo ? sanitizePersonalInfo(state.personalInfo) : null,
        selectedServices: state.selectedServices,
        configuredPlans: state.configuredPlans,
        onitPlan: state.onitPlan,
        // Persist all checkout-critical fields to survive page reloads
        appFields: state.appFields,
        uploadFlags: state.uploadFlags,
        onitSelected: state.onitSelected,
        planOptionsQueue: state.planOptionsQueue,
        propertyData: state.propertyData,
        waterAvailable: state.waterAvailable,
        waterDeclined: state.waterDeclined,
        isApartment: state.isApartment,
        apartmentWaterChoice: state.apartmentWaterChoice,
      }),
    }
  )
);

// One-time migration from localStorage to sessionStorage
function migrateToSessionStorage() {
  const STORAGE_KEY = 'order-storage';
  const MIGRATION_FLAG = 'order-storage-migrated-v1';

  if (sessionStorage.getItem(MIGRATION_FLAG)) return;

  const localData = localStorage.getItem(STORAGE_KEY);
  if (localData) {
    try {
      sessionStorage.setItem(STORAGE_KEY, localData);
      localStorage.removeItem(STORAGE_KEY); // Remove PII from persistent storage
      logger.info({ area: 'store' }, 'Moved order-storage to sessionStorage');
    } catch (e) {
      logger.error({ area: 'store' }, 'Migration failed', e instanceof Error ? e : undefined);
    }
  }

  sessionStorage.setItem(MIGRATION_FLAG, 'true');
}

migrateToSessionStorage();
